import { useRef } from "react";
import { useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { CASE_STUDIES } from "@/data/site";

const PanImage = ({ src, alt }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-20% 0px -20% 0px" });
  return (
    <img
      ref={ref}
      src={src}
      alt={alt}
      loading="lazy"
      className={`w-full h-full object-cover case-pan group-hover:scale-[1.03] transition-transform duration-700 ease-out ${
        inView ? "is-active" : ""
      }`}
    />
  );
};

const CaseStudies = () => (
  <main data-testid="case-studies-page" className="pt-20">
    <section className="relative py-24 lg:py-36 overflow-hidden grid-bg">
      <div className="absolute -top-32 right-0 w-[28rem] h-[28rem] bg-cyan-400/10 blur-[140px] rounded-full" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <MaskedLine delay={0.1}>
          <Overline>Case Studies</Overline>
        </MaskedLine>
        <h1 className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl mt-6">
          <MaskedLine delay={0.25}>Work that</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-stroke">pulls weight</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6} className="max-w-xl">
          <p className="text-zinc-400 text-base sm:text-lg leading-relaxed mt-8">
            Real platforms, live in the wild. Seven industries, seven bespoke builds —
            each one engineered to move its market.
          </p>
        </MaskedLine>
      </div>
    </section>

    <section data-testid="case-studies-list">
      {CASE_STUDIES.map((cs, i) => {
        const dark = i % 2 === 1;
        return (
          <div
            key={cs.slug}
            className={`py-24 lg:py-32 ${dark ? "bg-black text-white" : "bg-white text-black"}`}
          >
            <Reveal className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
              <article
                data-testid={cs.testId}
                className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center"
              >
                <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <a
                    href={cs.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group block relative border rounded-xl overflow-hidden ${dark ? "border-zinc-800" : "border-zinc-200"}`}
                  >
                    <div className="absolute top-0 left-0 right-0 h-9 bg-[#0A0A0C] border-b border-zinc-800 flex items-center gap-2 px-4 z-10">
                      <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                      <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                      <span className="w-2.5 h-2.5 rounded-full bg-cyan-400" />
                      <span className="ml-3 font-mono-accent text-[10px] text-zinc-500 tracking-wider">
                        {cs.domain}
                      </span>
                    </div>
                    <div className="aspect-[16/10] overflow-hidden pt-9">
                      <PanImage
                        src={cs.imageFull}
                        alt={`${cs.client} website built by Market Rounds`}
                      />
                    </div>
                  </a>
                </div>
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <span className={`font-display font-extrabold text-6xl select-none ${dark ? "text-stroke" : "text-stroke-dark"}`}>
                    0{i + 1}
                  </span>
                  <p className={`font-mono-accent text-[10px] uppercase tracking-[0.3em] mt-4 ${dark ? "text-cyan-400" : "text-cyan-600"}`}>
                    {cs.category}
                  </p>
                  <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-3">
                    {cs.client}
                  </h2>
                  <p className={`text-sm sm:text-base leading-relaxed mt-5 ${dark ? "text-zinc-400" : "text-zinc-600"}`}>
                    {cs.summary}
                  </p>
                  <ul className="mt-6 space-y-2">
                    {cs.impact.map((item) => (
                      <li key={item} className={`flex items-center gap-3 text-sm ${dark ? "text-zinc-300" : "text-zinc-700"}`}>
                        <span className="w-1.5 h-1.5 bg-cyan-400 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-2 mt-6">
                    {cs.tags.map((tag) => (
                      <span
                        key={tag}
                        className={`border px-3 py-1.5 font-mono-accent text-[10px] uppercase tracking-[0.2em] ${dark ? "border-zinc-800 text-zinc-400" : "border-zinc-300 text-zinc-600"}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <a
                    href={cs.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`visit-site-${cs.slug}`}
                    className={`inline-flex items-center gap-2 mt-8 border px-6 py-3.5 font-display font-bold text-xs uppercase tracking-wider hover:border-cyan-400 hover:text-cyan-500 transition-colors ${dark ? "border-zinc-700" : "border-zinc-300"}`}
                  >
                    Visit live site <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
              </article>
            </Reveal>
          </div>
        );
      })}
    </section>
  </main>
);

export default CaseStudies;
